import { Component, OnInit } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { TherapeuticClassProductAutoCompleteItem } from "src/app/ui/models/domains/auto-complete/therapeutic-class-product-auto-complete-item";
import { TherapeuticClassProductAutoCompleteSearchProvider } from "src/app/ui/models/domains/auto-complete/therapeutic-class-product-auto-complete-search-provider";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { ProductSubtype } from "src/app/ui/models/domains/pbm/Benefit/product-subtype";
import { ProductType } from "src/app/ui/models/domains/pbm/Benefit/product-type";
import { TherapeuticClass } from "src/app/ui/models/domains/pbm/Benefit/therapeutic-class";
import { PriceRegime } from "src/app/ui/models/domains/pbm/product-management/price-regime";
import { ProductLabelInfo } from "src/app/ui/models/domains/pbm/product-management/product-label-info";
import { ProductUseValidity } from "src/app/ui/models/domains/pbm/product-management/product-use-validity";
import { TributaryCreditConcession } from "src/app/ui/models/domains/pbm/product-management/tributary-credit-concession";
import { UseType } from "src/app/ui/models/domains/pbm/product-management/use-type";
import { UserPermissions } from "src/app/ui/models/domains/pbm/user/user-permissions";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { ProductManagementService } from "src/app/ui/services/pbm/product-management.service";
import { TherapeuticClassService } from "src/app/ui/services/pbm/therapeutic-class.service";
import { UserService } from "src/app/ui/services/user.service";
import { Toast } from "../../../toast/toast";
import { ProductLabelInfoForm } from "./product-label-info-form";
import { UpdateProductValidator } from "./update-product-validator";
import { MatSelectChange } from "@angular/material/select";

@Component({
  selector: "edit-product",
  templateUrl: "./edit-product.component.html",
  styleUrls: ["./edit-product.component.scss"],
})
export class EditProductComponent implements OnInit {
  public form: ProductLabelInfoForm;
  private productTypes: ProductType[];
  private productSubTypes: ProductSubtype[];
  private productUseValidities: ProductUseValidity[];
  private priceRegimes: PriceRegime[];
  private useTypes: UseType[];
  private tributaryCreditConcessions: TributaryCreditConcession[];
  public therapeuticClassProvider = new TherapeuticClassProductAutoCompleteSearchProvider(this.therapeuticClassService);

  constructor(
    private featureViewService: FeatureViewService,
    private productManagementService: ProductManagementService,
    private therapeuticClassService: TherapeuticClassService,
    private loadingService: LoadingService,
    private router: Router,
    private toast: Toast,
    private userService: UserService,
  ) {}

  async ngOnInit() {
    this.featureViewService.activeFeatureView(FeatureViews.EditProduct, PageViews.ProductManagementPageView);
    const subscription = this.productManagementService.getProduct().subscribe(async (product) => {
      if (product) {
        const loadingRef = this.loadingService.beginLoading();
        try {
          await this.loadLists(product);
          this.form = new ProductLabelInfoForm();
          this.form.editProductFormGroup();
          this.form.loadFormGroup(
            product,
            this.productTypes,
            this.productSubTypes,
            this.productUseValidities,
            this.priceRegimes,
            this.useTypes,
            this.tributaryCreditConcessions,
          );
        } catch (error) {
          this.router.navigate([RoutePath.SearchProduct]);
        } finally {
          loadingRef.closeLoading();
        }
      } else {
        this.router.navigate([RoutePath.SearchProduct]);
      }
      subscription.unsubscribe();
    });
  }

  private async loadLists(product: ProductLabelInfo): Promise<void> {
    this.productTypes = await this.productManagementService.productTypes();
    this.productSubTypes = await this.productManagementService.productSubTypesByType(product.getProductType());
    this.productUseValidities = await this.productManagementService.productUseValidities();
    this.priceRegimes = await this.productManagementService.priceRegimes();
    this.useTypes = await this.productManagementService.useTypes();
    this.tributaryCreditConcessions = await this.productManagementService.tributaryCreditConcessions();
  }

  public getFormGroup(): UntypedFormGroup {
    return this.form && this.form.getFormGroup();
  }

  public getProductTypes(): ProductType[] {
    return this.productTypes;
  }

  public getProductSubTypes(): ProductSubtype[] {
    return this.productSubTypes;
  }

  public getProductUseValidities(): ProductUseValidity[] {
    return this.productUseValidities;
  }

  public getPriceRegimes(): PriceRegime[] {
    return this.priceRegimes;
  }

  // public getUseTypes(): UseType[] {
  //   return this.useTypes;
  // }

  public getTributaryCreditConcessions(): TributaryCreditConcession[] {
    return this.tributaryCreditConcessions;
  }

  public async onProductTypeChange(event: MatSelectChange): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    try {
      this.productSubTypes = await this.productManagementService.productSubTypesByType(event.value);
      this.form.clearSubTypeFormControl();
      this.form.enableSubTypeFormControl();
      if (this.productSubTypes.length === 0) {
        this.form.disableSubTypeFormControl();
        this.toast.showWarningToast(`Nenhum sutipo associado ao tipo de produto ${this.form.getFormGroup().value.productType.getValue()}`);
      }
    } catch (error) {
    } finally {
      loadingRef.closeLoading();
    }
  }

  public getAutoCompleteValue(value: TherapeuticClassProductAutoCompleteItem): void {
    const therapeuticClass = new TherapeuticClass();
    therapeuticClass.setTherapeuticClassKey(value.getTherapeuticClassKey());
    therapeuticClass.setDescription(value.getDescription());
    therapeuticClass.setCode(value.getCode());
    this.form.getFormGroup().patchValue({ therapeuticClass });
  }

  public goToSearchProduct(): void {
    this.router.navigate([RoutePath.SearchProduct]);
  }

  public editProductPricing(): void {
    this.router.navigate([RoutePath.EditProductPricing]);
  }

  public async saveChanges(): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    const validator = new UpdateProductValidator(this.form);
    if (validator.isFormValid()) {
      try {
        const productLabelInfo = this.form.parseFormGroup();
        await this.productManagementService.updateProduct(productLabelInfo);
        this.productManagementService.setProduct(productLabelInfo);
        this.toast.showSuccessToast("Alterações salvas com sucesso");
      } catch (error) {
        this.toast.showWarningToast(error);
      } finally {
        loadingRef.closeLoading();
      }
    } else {
      this.toast.showWarningToast(validator.getErrorMessage());
      loadingRef.closeLoading();
    }
  }

  public hasAccessToEditProduct(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditProduct);
  }
}
